import type { ComponentType, ReactNode, SVGAttributes } from "react";

import {
  Box,
  ChakraMenuButton,
  CheckIcon,
  FilterPillButton,
  Column,
  Menu,
  MenuButton,
  MenuDivider,
  MenuIconButton,
  MenuItem,
  MenuList,
  Row,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { type Appearance, FieldButton } from "./property-picker";

type StatusIconComponent = ComponentType<SVGAttributes<SVGElement>>;

export type StatusMenuOption = {
  value: string;
  label: string;
  icon: StatusIconComponent;
  /** Sets the option apart from the ones above it, as archiving is from a workflow's statuses. */
  isSeparate?: boolean;
};

type Props = {
  options: StatusMenuOption[];
  value: string;
  onChange: (value: string) => void;
  /** "badge" makes `valueDisplay` itself the button, with no chrome of its own. "chip" puts `valueDisplay` in a quiet round button, to echo a status chip drawn elsewhere. */
  appearance?: Appearance | "badge" | "filter-pill" | "chip";
  /** What the closed field or badge shows in place of the status's icon and name. */
  valueDisplay?: ReactNode;
  /** Shows only the icon, for dense rows. */
  iconOnly?: boolean;
  /** What the button shows when the value isn't among the options yet, as while they load. */
  fallback?: Pick<StatusMenuOption, "label" | "icon">;
};

/**
 * A status control. It is a menu rather than a searchable list: workflows
 * have a handful of statuses, and a menu keeps them in workflow order.
 */
export const StatusMenu = ({
  options,
  value,
  onChange,
  appearance = "pill",
  iconOnly = false,
  fallback,
  valueDisplay,
}: Props) => {
  const current = options.find((option) => option.value === value) ?? fallback;
  const label = current?.label ?? "No status";
  const ariaLabel = `Status: ${label}. Change status`;

  return (
    // Rows render one of these each; positioning on mount would lay out every closed menu.
    <Menu computePositionOnMount={false}>
      {appearance === "badge" ? (
        <ChakraMenuButton
          aria-label={ariaLabel}
          display="inline-flex"
          maxWidth="100%"
          borderRadius="full"
          _focusVisible={{ boxShadow: "outline", outline: "none" }}
        >
          {valueDisplay}
        </ChakraMenuButton>
      ) : appearance === "chip" ? (
        <MenuButton
          aria-label={ariaLabel}
          size="sm"
          shape="circle"
          variant="tertiary"
        >
          {valueDisplay}
        </MenuButton>
      ) : appearance === "filter-pill" ? (
        <ChakraMenuButton
          as={FilterPillButton}
          aria-label={ariaLabel}
          size="sm"
          label={label}
          summary={null}
          icon={current?.icon}
        />
      ) : appearance === "field" ? (
        <ChakraMenuButton as={FieldButton} aria-label={ariaLabel}>
          {valueDisplay ?? (
            // The menu button wraps its children in a span, so without a row the icon and label stack.
            <Row as="span" align="center" gap={1.5}>
              {current && <Box as={current.icon} flexShrink={0} />}
              <TextWithTooltip>{label}</TextWithTooltip>
            </Row>
          )}
        </ChakraMenuButton>
      ) : iconOnly && current ? (
        <MenuIconButton
          aria-label={ariaLabel}
          icon={current.icon}
          size="sm"
          variant="tertiary"
        />
      ) : (
        <MenuButton
          aria-label={ariaLabel}
          size="sm"
          variant={appearance === "pill" ? "secondary" : "tertiary"}
        >
          <Row as="span" align="center" gap={1.5} fontWeight="normal">
            {current && <Box as={current.icon} flexShrink={0} />}
            <TextWithTooltip>{label}</TextWithTooltip>
          </Row>
        </MenuButton>
      )}
      <MenuList>
        {options.map((option) => [
          option.isSeparate && <MenuDivider key={`${option.value}-divider`} />,
          <MenuItem
            key={option.value}
            icon={option.icon}
            onClick={() => {
              if (option.value !== value) onChange(option.value);
            }}
          >
            <Row align="center" gap={2} width="100%">
              <Column flex={1} minWidth={0}>
                <Text>{option.label}</Text>
              </Column>
              {option.value === value && (
                <>
                  <Box as={CheckIcon} boxSize={4} flexShrink={0} aria-hidden />
                  <Box as="span" srOnly>
                    Current status
                  </Box>
                </>
              )}
            </Row>
          </MenuItem>,
        ])}
      </MenuList>
    </Menu>
  );
};
